import { useEffect, useRef, useState, type ReactNode } from 'react';
import CustomButton from '../../components/customButton/CustomButton';
import RegistrationPage, {
    CONDITIONS_ACTIONS_CLASS,
    CONDITIONS_FADE_CLASS,
    CONDITIONS_SCROLL_DOCK_AT_BOTTOM_CLASS,
    CONDITIONS_SCROLL_DOCK_CLASS,
} from '../../components/layouts/RegistrationPage';
import { PAGE_HEADER_TITLE_LG_CLASS, PAGE_HEADER_TITLE_CLASS } from '../../components/PageHeader';
import { useCurrentLang } from '../../hooks/useCurrentLang';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { readTerms } from '../../services/registration';
import { getAccountId } from '../../utils/storage';
import ArmenianFile from '../../components/files/ArmenianFile';
import EnglishFile from '../../components/files/EnglishFile';
import RussianFile from '../../components/files/RussianFile';
import useLogger from '../../hooks/useLogger';
import { TERMS_ACTIONS, LOG_STATUSES } from '../../logging/LoggingConstants';
import {
    AnalyticsEvent,
    markContractStarted,
    markOtpStarted,
    trackRegistrationFailed,
    useAnalytics,
} from '../../analytics';
import { handleApiError } from '../../utils/apiError';
import { cn } from '../../utils/cn';
import Checkbox from '../../components/form/Checkbox';
import PageLoaderOverlay from '../../components/loading/PageLoaderOverlay';

const urlGeneral = {
    en: 'https://ardshinbank.am/general-terms-of-banking-services?lang=en',
    ru: 'https://ardshinbank.am/general-terms-of-banking-services?lang=ru',
    am: 'https://ardshinbank.am/general-terms-of-banking-services?lang=hy',
};

const urlConditions = {
    en: 'https://www.ardshinbank.am/for-you/accounts?lang=en',
    ru: 'https://www.ardshinbank.am/for-you/accounts?lang=ru',
    am: 'https://www.ardshinbank.am/for-you/accounts?lang=hy',
};

const termsByLanguage = {
    en: <EnglishFile />,
    am: <ArmenianFile />,
    ru: <RussianFile />,
};

const READ_TERMS_CODE = 55555;

export default function Conditions() {
    const accountId = getAccountId();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { log } = useLogger();
    const { track } = useAnalytics();
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [checkedFirst, setCheckedFirst] = useState(false);
    const [checkedSecond, setCheckedSecond] = useState(false);
    const [checkedThird, setCheckedThird] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [atBottom, setAtBottom] = useState(false);
    const [canScroll, setCanScroll] = useState(false);
    const atBottomRef = useRef(false);
    const canScrollRef = useRef(false);
    const lang = useCurrentLang() as 'en' | 'ru' | 'am';
    const termsContent = termsByLanguage[lang] ?? termsByLanguage.am;
    const allChecked = checkedFirst && checkedSecond && checkedThird;

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) {
            return undefined;
        }

        const BOTTOM_ENTER_PX = 24;
        const BOTTOM_LEAVE_PX = 80;

        const updateScrollState = () => {
            const { scrollTop, scrollHeight, clientHeight } = el;
            const scrollable = scrollHeight - clientHeight > 8;
            const distanceFromBottom = scrollHeight - clientHeight - scrollTop;

            if (scrollable !== canScrollRef.current) {
                canScrollRef.current = scrollable;
                setCanScroll(scrollable);
            }

            let nextAtBottom: boolean;
            if (!scrollable) {
                nextAtBottom = true;
            } else if (atBottomRef.current) {
                nextAtBottom = distanceFromBottom <= BOTTOM_LEAVE_PX;
            } else {
                nextAtBottom = distanceFromBottom <= BOTTOM_ENTER_PX;
            }

            if (nextAtBottom !== atBottomRef.current) {
                atBottomRef.current = nextAtBottom;
                setAtBottom(nextAtBottom);
            }
        };

        el.addEventListener('scroll', updateScrollState, { passive: true });
        updateScrollState();

        let resizeObserver: ResizeObserver | undefined;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(updateScrollState);
            resizeObserver.observe(el);
        }

        return () => {
            resizeObserver?.disconnect();
            el.removeEventListener('scroll', updateScrollState);
        };
    }, [termsContent]);

    const handleScrollButton = () => {
        const el = scrollRef.current;
        if (!el) {
            return;
        }
        el.scrollTo({
            top: atBottom ? 0 : el.scrollHeight,
            behavior: 'smooth',
        });
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!accountId) return;
        setIsLoading(true);
        readTerms(accountId, READ_TERMS_CODE)
            .then(() => {
                log(TERMS_ACTIONS.READ_TERMS, LOG_STATUSES.SUCCESS);
                track(AnalyticsEvent.CONTRACT_TERMS_ACCEPTED);
                markContractStarted();
                markOtpStarted('contract');
                track(AnalyticsEvent.OTP_SENT, {
                    otp_type: 'contract',
                    attempt: 1,
                    is_resend: false,
                    resend_count: 0,
                });
                navigate('/otp');
            })
            .catch((e) => {
                trackRegistrationFailed({
                    step: 'contract',
                    error: e,
                });
                handleApiError(e, navigate);
            })
            .finally(() => setIsLoading(false));
    };

    const scrollButton = (
        <button
            type="button"
            aria-label={atBottom ? 'Scroll to top' : 'Scroll to bottom'}
            className="flex h-12 w-12 shrink-0 cursor-pointer items-center justify-center rounded-full border-2 border-primary bg-primary/10 shadow-sm transition"
            onClick={handleScrollButton}
        >
            <svg
                className={cn(
                    'transition-transform duration-300 ease-out',
                    atBottom && 'rotate-180'
                )}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M3 8L12 17L21 8"
                    stroke="#0A64C8"
                    stroke-width="2"
                    stroke-linecap="round"
                />
            </svg>
        </button>
    );

    const showFade = canScroll && !atBottom;

    return (
        <>
        <PageLoaderOverlay active={isLoading} />
        <RegistrationPage
            layout="conditions"
            contentRef={scrollRef}
            accessory={showFade ? <div className={CONDITIONS_FADE_CLASS} aria-hidden /> : null}
            conditionsDock={
                canScroll ? (
                    <div
                        className={cn(
                            CONDITIONS_SCROLL_DOCK_CLASS,
                            atBottom && CONDITIONS_SCROLL_DOCK_AT_BOTTOM_CLASS,
                        )}
                    >
                        {scrollButton}
                    </div>
                ) : null
            }
        >
            <h2 className={cn(PAGE_HEADER_TITLE_CLASS, PAGE_HEADER_TITLE_LG_CLASS, '')}>
                {t('conditionsTitle')}
            </h2>

            <div className="text-xs !leading-relaxed">{termsContent}</div>

            <form id="conditions-form" onSubmit={handleSubmit} className={cn(CONDITIONS_ACTIONS_CLASS, "px-1")}>
                <Checkbox
                    name="conditions-general-terms"
                    checked={checkedFirst}
                    onChange={setCheckedFirst}
                    label={
                        <>
                            {t('conditionsLiFirst')}{' '}
                            <a
                                href={urlGeneral[lang]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary underline decoration-primary/40 underline-offset-2"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {t('conditionsLiFirstА')}
                            </a>
                        </>
                    }
                />

                <Checkbox
                    name="conditions-account-terms"
                    checked={checkedSecond}
                    onChange={setCheckedSecond}
                    label={
                        <>
                            {' '}
                            {t('conditionsLiFirst')}{' '}
                            <a
                                href={urlConditions[lang]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary underline decoration-primary/40 underline-offset-2"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {t('conditionsLiSecond')}
                            </a>
                        </>
                    }
                />

                <Checkbox
                    name="conditions-accept-all"
                    checked={checkedThird}
                    onChange={setCheckedThird}
                    label={t('accsept')}
                />

                <CustomButton
                    className="w-full mt-3"
                    disabled={!allChecked}
                    form="conditions-form"
                >
                    {t('conditionsButtonYes')}
                </CustomButton>
            </form>
        </RegistrationPage>
        </>
    );
}
